import * as React from 'react';
import { ArrayMinOne } from '@estee/elc-universal-utils';
import { getRootMethods, ITranslations, requestTranslations } from './utils';
import {
    Context,
    ContextValueType,
    IProps as UpdateContextProvideProps,
    UpdateContextProvider
} from './Context';
import { ContextMerger } from './ContextMerger';

type TranslationsType = { translations: ITranslations };

interface IProps extends UpdateContextProvideProps<ITranslations> {
    translationFields: ArrayMinOne<string>;
}

class GetTranslation extends UpdateContextProvider<ITranslations, IProps> {
    static displayName = 'GetTranslation';

    constructor(props: IProps) {
        super(props);

        const translations = getRootMethods().getTranslations(props.translationFields);
        if (translations) {
            this.hasValue = true;
            this.updateContextProvider({ translations });
        }
    }

    public async componentDidMount() {
        const { translationFields } = this.props;

        if (!this.hasValue) {
            const translationsResponse = await requestTranslations(translationFields);

            if (translationsResponse) {
                this.hasValue = true;
                getRootMethods().addTranslations(translationsResponse);

                this.updateContextProvider({ translations: translationsResponse });
            }
        }
    }
}

type IAddTranslationProps = { translations?: ITranslations };

export function addTranslation(translationFields: ArrayMinOne<string>) {
    getRootMethods().registerTranslationFields(translationFields);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any => (props: IAddTranslationProps) => (
        <Context.Consumer>
            {({ updateProvider, translations }: ContextValueType<TranslationsType>) => (
                <GetTranslation
                    translationFields={translationFields}
                    updateContextProvider={updateProvider}
                    contextHasValue={Boolean(translations)}
                >
                    <WrappedComponent {...props} />
                </GetTranslation>
            )}
        </Context.Consumer>
    );
}

type IConsumerProps = { translations?: ITranslations };

export function translationConsumer(translationFields: ArrayMinOne<string>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (WrappedComponent: any): any =>
        class extends React.Component<IConsumerProps> {
            static displayName = 'translationConsumer';

            public render() {
                const translationsFromProps = this.props.translations;

                return (
                    <Context.Consumer>
                        {({
                            translations: translationsFromContext
                        }: ContextValueType<TranslationsType>) => (
                            <ContextMerger
                                object={translationsFromContext || {}}
                                keys={translationFields}
                                render={(requestedTranslations: ITranslations) => (
                                    <WrappedComponent
                                        {...this.props}
                                        translations={{
                                            ...requestedTranslations,
                                            ...translationsFromProps
                                        }}
                                    />
                                )}
                            />
                        )}
                    </Context.Consumer>
                );
            }
        };
}
